/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import moment from 'moment';
/* eslint-disable @nx/enforce-module-boundaries */
import { logger } from '@marriott/mi-headless-utils';
import { getLocalizedDate } from '@marriott/mi-ui-library';
import { constructJsonSchema } from 'libs/mi-leisure-components/src/utils/generateJsonSchemaUtils';
import {
  EXTERNAL_CTA_TYPE,
  HOTEL_VIEW,
  MARRIOTT_CACHE_URL,
  outputInterpolation,
  outputQuality,
  VIEW_RATE_LABEL,
  METERSTOKM,
  METERSTOMILES,
  DESTINATION_FEE_CODE_DESCRIPTION,
  INCLUDES_LABEL,
  RESORT_FEE_DESCRIPTION,
  SURCHARGE_ORDINANCE_COST_DESCRIPTION,
  DESTINATION_FEE_DESCRIPTION,
  TOURISM_MARKETING_FEE_DESCRIPTION,
  HOTEL_MARKETING_CAPTION,
  WARN_SEARCH_1009,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
  NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
  WARN_CODE_1001,
  SM_VIEW_HOTEL_WEBSITE_LINK,
} from '../../../constants/CommonConstants';
import {
  milesToMeters,
  isValidNumberString,
  removeParamsFromURL,
  addSubDirectoryPrefix,
} from '../../../utils/CommonUtils';
import { RECENT_SEARCH_LIST_LIMIT } from '../../../constants/CommonConstants';

import {
  constructMandatoryFeeTitle,
  constructToolTipTitle,
  getHotelCodeUnavailable,
  identifyTypeOfMandatoryFee,
} from './ratesHelper';
import { getCustomFormattedDate, getDateFormatted } from './dateUtils';

// Common utility to build a formatted hotel slug from hotelId and hotelName
export const getFormattedHotelSlug = (hotelId?: string, hotelName?: string): string => {
  if (!hotelId || !hotelName) return '';
  return (
    hotelId.toLowerCase() +
    '-' +
    hotelName
      .replace('-', ' ')
      .replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-')
      .replace(/^(-)+|(-)+$/g, '')
      .toLowerCase()
  );
};

// Common utility to build property page URL based on brand and subdirectory
export const buildPropertyPageURL = (
  brandId: string,
  seoNickname: string,
  subdirectory: string,
  pathSuffix: string
): string => {
  if (brandId === 'RZ') {
    return `https://www.ritzcarlton.com/en/hotels/${seoNickname}/overview/`;
  }
  const prefix = subdirectory ? `/${subdirectory}` : '/en-us';
  return `${prefix}${pathSuffix}`;
};

// Set recently viewed properties utility
export function setRecentlyViewedProperties(property: any) {
  const name = property?.basicInformation?.name;
  const localViewed = localStorage.getItem('miRecentlyViewedProperties');
  const recentViewed: any = JSON.parse(
    localViewed
      ? localViewed
      : JSON.stringify({
          config: {
            maxCachedPropertiesLimit: '10',
            maxCachedPropertiesTab: '2',
            maxCachedPropertiesDesktop: '3',
            maxCachedPropertiesMobile: '2',
            maxCachedDaysLimit: '60',
          },
          recentlyViewedProperties: [],
        })
  );
  const isDuplicate =
    recentViewed?.recentlyViewedProperties?.length &&
    recentViewed.recentlyViewedProperties.find((item: any) => item.name === name);

  if (!isDuplicate) {
    const obj = {
      name: name,
      address: name,
    };

    if (recentViewed && recentViewed?.recentlyViewedProperties?.length >= 0) {
      if (recentViewed.recentlyViewedProperties?.length >= RECENT_SEARCH_LIST_LIMIT) {
        recentViewed.recentlyViewedProperties.pop();
      }
      recentViewed.recentlyViewedProperties.unshift(obj);
    }

    localStorage.setItem('miRecentlyViewedProperties', JSON.stringify(recentViewed));
  }
}
// Helper to handle sort event and apply filter
export function handleSortByEvent({
  event,
  sortOption,
  ariesOffer,
  handleApplyFilter,
}: {
  event: any;
  sortOption: string;
  ariesOffer: any;
  handleApplyFilter: (...args: any[]) => void;
}) {
  if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
    handleApplyFilter(
      ariesOffer?.['selectedBrands'],
      ariesOffer?.['selectedAmenities'],
      ariesOffer?.['selectedTransportaion'],
      ariesOffer?.['selectedHotelType'],
      ariesOffer?.['selectedEvents'],
      ariesOffer?.['selectedActivities'],
      ariesOffer?.['selectedPrices'],
      ariesOffer?.['selectedCurrency'],
      ariesOffer?.['selectedCity'],
      ariesOffer?.['selectedState'],
      ariesOffer?.['includeUnavailableProperties'],
      ariesOffer?.['showPricesTax'],
      sortOption
    );
    //Remove pg query param from url
    removeParamsFromURL('pg', true);
  }
}
// Footer links helper for property card
export function getFooterLinks({
  ratePage,
  model,
  hotel,
  rate,
}: {
  ratePage: string;
  model: any;
  hotel: any;
  rate: number | null | undefined;
}) {
  return {
    href: ratePage,
    target: '_self',
    ratesClickTrackValue: `${model?.id}|${hotel?.node?.property?.id}-${model?.bookNow}|${EXTERNAL_CTA_TYPE}`,
    rel: 'noreferrer',
    className: 'm-button-s m-button-primary custom_click_track reserve-cta-button',
    text: `${model?.bookNow}`,
    isLink: false,
    hasPrice: typeof rate === 'number' && rate > 0 ? true : false,
    priceAriaLabel: '',
    priceClickTrackValue: `${model?.id}|${hotel?.node?.property?.id}-${model?.storefrontPropertyPointsPerStay}|${EXTERNAL_CTA_TYPE}`,
    priceValue: rate ?? null,
    priceType: rate && `${hotel?.node?.property?.basicInformation?.currency} / ${model?.storefrontPropertyNight}`,
    currency: hotel?.node?.property?.basicInformation?.currency,
    unavailableRateText: model?.rateUnavailable,
  };
}

export function getCalculatedDistance(dist: number | undefined, km: boolean, lang?: string): string | null {
  /** calculated distance for km/ miles */
  if (dist) {
    if (km) {
      // convert meters to km
      return formatNumberByLocale((dist / METERSTOKM).toFixed(1), lang || '');
    } else {
      // convert meters to miles
      return formatNumberByLocale((dist / METERSTOMILES).toFixed(1), lang || '');
    }
  }
  return null;
}
export const getSortByVal = (sortBySessionVal: any, destinationData: any): string => {
  if (sortBySessionVal) {
    return sortBySessionVal;
  } else {
    if (destinationData?.isCountryEnabled || destinationData?.isStateEnabled) {
      return 'CITY';
    } else {
      return 'DISTANCE';
    }
  }
};
export const getSortByOptions = (model: any, isStateOrCountryEnabled: boolean, totalprop: any) => {
  let optionsList = [
    {
      sortByOptionTitle: model?.priceLowToHighLabel,
      sortByOptionCode: 'PRICE',
    },
    {
      sortByOptionTitle: model?.cityLabel,
      sortByOptionCode: 'CITY',
    },
    {
      sortByOptionTitle: model?.storefrontPropertyBrandFilter,
      sortByOptionCode: 'BRAND',
    },
    {
      sortByOptionTitle: model?.guestRatingLabel,
      sortByOptionCode: 'GUEST_RATINGS',
    },
    {
      sortByOptionTitle: model?.noOfReviewsLabel,
      sortByOptionCode: 'NUM_REVIEWS',
    },
  ];

  // If isStateOrCountryEnabled, filter options based on totalProperties
  if (isStateOrCountryEnabled) {
    if (totalprop != null && totalprop > 200) {
      optionsList = optionsList.filter(option => option.sortByOptionCode !== 'PRICE'); // Exclude 'Price'
    }
  } else {
    optionsList.unshift({
      sortByOptionTitle: model?.distanceLabel,
      sortByOptionCode: 'DISTANCE',
    });
  }
  return optionsList;
};
export const isCityDestination = (destinationMetaData: any) => {
  return (
    !destinationMetaData?.isCountryEnabled &&
    !isCountryCodeOverridden(destinationMetaData) &&
    !destinationMetaData?.isStateEnabled
  );
};
const isCountryCodeOverridden = (destinationMetaData?: any) => {
  return destinationMetaData?.countryCodeOverride ? true : false;
};
export const getCountryCode = (destinationMetaData: any) => {
  return destinationMetaData?.countryCodeOverride ?? destinationMetaData?.countryCode;
};
export const buildFacetTerms = (
  brandFacets: any,
  amentiesFacets: any,
  hotelTypeFacets: any,
  activitiesFacets: any,
  eventsFacets: any,
  transportaionFacets: any
) => {
  return [
    {
      type: 'BRANDS',
      dimensions: brandFacets,
    },
    {
      type: 'AMENITIES',
      dimensions: amentiesFacets,
    },
    {
      type: 'PROPERTY_TYPES',
      dimensions: hotelTypeFacets,
    },
    {
      type: 'ACTIVITIES',
      dimensions: activitiesFacets,
    },
    {
      type: 'MEETINGS_EVENTS',
      dimensions: eventsFacets,
    },
    {
      type: 'TRANSPORTATION_TYPES',
      dimensions: transportaionFacets,
    },
  ];
};

export const getSearchResultQuery = (
  searchData: any,
  destinationMetaData: any,
  offset: any,
  brandFacets: any,
  limit: any,
  sortBy: any,
  amentiesFacets: any,
  transportaionFacets: any,
  hotelTypeFacets: any,
  eventsFacets: any,
  activitiesFacets: any,
  priceDimension: any,
  cityFacets: any,
  stateFacets: any,
  includeUnavailableProperties: boolean,
  showPricesTax: boolean,
  currentLocale: any,
  mandatoryFeesEnabled: boolean,
  sersidecall?: boolean,
  serverSideoffset?: any,
  ariesOffer?: any,
  countryCode?: any,
  hasdestinationChanged?: any
): any => {
  // Original payload for leisure renderer
  const taxFlag = currentLocale === 'en-US' && countryCode !== 'GB' && countryCode !== 'JP' ? false : true; // currentLocale === es-ES
  const countryCodeVal = getCountryCode(destinationMetaData);
  const showPricesTaxServer = hasdestinationChanged ? taxFlag : ariesOffer?.['showPricesTax'] ?? taxFlag;
  const includeTaxesAndFees = sersidecall ? showPricesTaxServer : showPricesTax;
  const includeMandatoryFees = mandatoryFeesEnabled;
  console.log(includeMandatoryFees, currentLocale, 'EHH!!');
  const sessionBrandFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedBrands']
    ? ariesOffer?.['selectedBrands']?.map((item: any) => item.code)
    : [];
  const sessionAmenitiesFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedAmenities']
    ? ariesOffer?.['selectedAmenities']?.map((item: any) => item?.code)
    : [];
  console.log('[destination] server - facets >>', sessionAmenitiesFacetList);
  console.log('[destination] server - sersidecall >>', sersidecall);
  const sessionTransportaionFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedTransportaion']
    ? ariesOffer?.['selectedTransportaion'].map((item: any) => item?.code)
    : [];

  const sessionHotelTypeFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedHotelType']
    ? ariesOffer?.['selectedHotelType'].map((item: any) => item?.code)
    : [];

  const sessionEventsFaceList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedEvents']
    ? ariesOffer?.['selectedEvents'].map((item: any) => item?.code)
    : [];

  const sessionActivitiesFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedActivities']
    ? ariesOffer?.['selectedActivities'].map((item: any) => item?.code)
    : [];

  const sessionCitiesFacetList = hasdestinationChanged
    ? []
    : ariesOffer && ariesOffer?.['selectedCity']
    ? ariesOffer?.['selectedCity'].map((item: any) => item?.code)
    : [];

  const priceRange = hasdestinationChanged
    ? 'Any Price'
    : ariesOffer && ariesOffer?.['selectedPrices']
    ? ariesOffer?.['selectedPrices']?.[0]?.label
    : 'Any Price';

  const serverSideAvailibilityFlag = hasdestinationChanged
    ? true
    : (ariesOffer && ariesOffer?.['includeUnavailableProperties']) ?? true;

  const keepDefaultFilters =
    destinationMetaData?.amenities?.length > 0 ||
    destinationMetaData?.activities?.length > 0 ||
    destinationMetaData?.hotelType?.length > 0;

  const defaultAminities = keepDefaultFilters
    ? (destinationMetaData?.amenities || []).concat(sessionAmenitiesFacetList)
    : sessionAmenitiesFacetList;
  const defaultActivities = keepDefaultFilters
    ? (destinationMetaData?.activities || []).concat(sessionActivitiesFacetList)
    : sessionActivitiesFacetList;
  const defaultHotelType = keepDefaultFilters
    ? (destinationMetaData?.hotelType || []).concat(sessionHotelTypeFacetList)
    : sessionHotelTypeFacetList;

  const key = priceRange as keyof typeof priceDimensionMapping;
  const priceDimensionVal = priceDimensionMapping[key];
  const sortDirection = sortBy === 'GUEST_RATINGS' || sortBy === 'NUM_REVIEWS' ? 'DESC' : 'ASC';

  const facetTerms = buildFacetTerms(
    sersidecall ? sessionBrandFacetList : brandFacets,
    sersidecall ? defaultAminities : amentiesFacets,
    sersidecall ? defaultHotelType : hotelTypeFacets,
    sersidecall ? defaultActivities : activitiesFacets,
    sersidecall ? sessionEventsFaceList : eventsFacets,
    sersidecall ? sessionTransportaionFacetList : transportaionFacets
  );

  const obj: any = {
    search: {
      options: {
        startDate: searchData?.startDate,
        endDate: searchData?.endDate,
        includeUnavailableProperties: sersidecall ? serverSideAvailibilityFlag : includeUnavailableProperties,
        includeTaxesAndFees: includeTaxesAndFees,
        numberInParty: searchData?.numberInParty,
        quantity: searchData?.quantity,
        childAges: searchData?.childAges,
        rateRequestTypes: [
          {
            type: 'STANDARD',
            value: '',
          },
        ],
        includeMandatoryFees: includeMandatoryFees,
      },
      facets: {
        terms: facetTerms,
        ranges: [
          {
            type: 'PRICE',
            dimensions: sersidecall ? priceDimensionVal : priceDimension,
            endpoints: ['0', '100', '200', 'overflow'],
          },
          {
            type: 'DISTANCE',
            dimensions: [],
            endpoints: ['0', '5000', '15000', '80000'],
          },
        ],
      },
      ...(isCityDestination(destinationMetaData) &&
        destinationMetaData?.radius !== null &&
        isValidNumberString(destinationMetaData.radius) && { distance: milesToMeters(destinationMetaData.radius) }),
    },
    sort: {
      fields: [
        {
          field: sortBy,
          direction: sortDirection,
        },
      ],
    },
    filter: [
      HOTEL_MARKETING_CAPTION,
      RESORT_FEE_DESCRIPTION,
      DESTINATION_FEE_DESCRIPTION,
      SURCHARGE_ORDINANCE_COST_DESCRIPTION,
      TOURISM_MARKETING_FEE_DESCRIPTION,
    ],
    phoneFilter: ['PHONE'],
    modes: ['LOWEST_AVERAGE_NIGHTLY_RATE', 'POINTS_PER_UNIT'],
    offset: sersidecall ? serverSideoffset : offset,
    limit: limit,
    limitPerCategory: 2,
  };

  interface filterType {
    type: string;
    dimensions: any;
  }
  interface sortType {
    field: string;
    direction: string;
  }
  if (isCityDestination(destinationMetaData)) {
    obj.search['latitude'] = parseFloat(destinationMetaData?.latitude ?? 0);
    obj.search['longitude'] = parseFloat(destinationMetaData?.longitude ?? 0);
  }
  const filterDetails: filterType[] = [
    {
      type: 'CITIES',
      dimensions: sersidecall ? sessionCitiesFacetList : cityFacets,
    },
    {
      type: 'STATES',
      dimensions: destinationMetaData?.isStateEnabled ? [destinationMetaData?.stateCode] : stateFacets,
    },
    {
      type: 'COUNTRIES',
      dimensions: !isCityDestination(destinationMetaData) && countryCodeVal ? [countryCodeVal] : [],
    },
  ];
  obj.search.facets.terms.push(...filterDetails);
  return obj;
};

const getTitle = (property: any, brandId: string, model: any, subdirectory: any) => {
  const seoNickname = property?.seoNickname ?? '';
  const slug = seoNickname || getFormattedHotelSlug(property?.id, property?.basicInformation?.name);
  const titleLink = buildPropertyPageURL(brandId, slug, subdirectory, `${SM_VIEW_HOTEL_WEBSITE_LINK}${slug}`);
  const titleDetails = {
    title: property?.basicInformation?.name,
    titleAriaLabel: property?.basicInformation?.name,
    isTitleLink: true,
    titleClickCallback: () => {
      window.open(titleLink, '_blank');
    },
    titleLink,
    titleClickTrackVal: `${model?.id}|${property?.id}|${EXTERNAL_CTA_TYPE}`,
  };
  return titleDetails;
};

const getReviewDetails = (
  property: any,
  brandId: string,
  distance: any,
  model: any,
  subdirectory: any,
  lang: string
) => {
  const seoNickname = property?.seoNickname;
  const rating = property?.reviews?.stars?.count ?? 0;
  const reviewCount = property?.reviews?.numberOfReviews?.count ?? 0;
  const reviewUrl =
    brandId === 'RZ'
      ? `https://www.ritzcarlton.com/en/hotels/${seoNickname}/overview/`
      : (subdirectory ? `/${subdirectory}` : '/en-us') + `/hotels/${seoNickname}/reviews/`;
  const distanceInMiles = getCalculatedDistance(distance, lang !== 'en-US', lang);
  const reviewDetails = {
    ...(rating !== 0 && { reviewsAvg: rating }),
    ...(reviewCount !== 0 && { reviewsText: `(${reviewCount} ${model?.storefrontPropertyReviews})` }),
    reviewsLink: reviewUrl,
    reviewsLabel: '',
    reviewsClickTrackValue: `${model?.id}|${property?.id}-${model?.storefrontPropertyReviews}|${EXTERNAL_CTA_TYPE}`,
    milesText:
      distanceInMiles !== null && distanceInMiles !== undefined
        ? `${distanceInMiles} ${model?.unitFromDestinationLabel}`
        : '',
  };
  return reviewDetails;
};
export const getDescriptionText = (descriptions: any) => {
  return descriptions?.find((desc: any) => desc?.type?.enumCode === HOTEL_MARKETING_CAPTION)?.text || '';
};

const getImageSetup = (viewPort: any) => {
  let imageDimensions = {
    height: 222,
    width: 715,
  };
  if (viewPort === 'mobile') {
    imageDimensions = {
      height: 240,
      width: 360,
    };
  } else {
    imageDimensions = {
      height: 222,
      width: 715,
    };
  }
  return imageDimensions;
};
const getImageProps = (viewPort: any): string => {
  const imageDimensions = getImageSetup(viewPort);
  const imgProp =
    `?output-quality=${outputQuality}&interpolation=${outputInterpolation};*,*&downsize=` +
    imageDimensions.width +
    'px:*';
  return imgProp;
};
export const getPropertyImages = (data: any, viewPort: any) => {
  const imageProperties = getImageProps(viewPort);
  const photoGalleryObj = data?.media?.photoGallery;
  const propertyImageList: any = [];
  const propertyImageArray: any = [];
  let cauroselImages: any = [];
  const edgesList: any = [];

  if (photoGalleryObj) {
    Object.keys(photoGalleryObj).forEach((categoryKey: any) => {
      const propertyObject: any = [];
      if (categoryKey !== '__typename' && photoGalleryObj[categoryKey]) {
        propertyObject['categoryCode'] = categoryKey;
        propertyObject['photosObj'] = photoGalleryObj[categoryKey];
        propertyImageList.push(propertyObject);
      }
    });
  }
  if (data?.media?.primaryImage?.edges?.[0]?.node?.imageUrls) {
    const hotelViewObj: any = [];
    hotelViewObj['categoryCode'] = HOTEL_VIEW;
    hotelViewObj['photosObj'] = data?.media?.primaryImage;
    propertyImageList.unshift(hotelViewObj);
  }
  propertyImageList.forEach((edgeCollection: any) => {
    edgesList.push(edgeCollection?.photosObj?.edges);
    edgesList.forEach((photosList: any) => {
      photosList.forEach((imgNode: any) => {
        const wideImg = imgNode?.node?.imageUrls?.wideHorizontal;
        const altText = imgNode?.node?.alternateDescription || '';
        if (wideImg !== null && wideImg !== '') {
          propertyImageArray.push({
            defaultImageUrl: MARRIOTT_CACHE_URL + wideImg + imageProperties,
            altText: altText,
          });
          cauroselImages = Array.from(
            new Map(propertyImageArray.map((item: any) => [item.defaultImageUrl, item])).values()
          ).slice(0, 10);
        }
      });
    });
  });
  if (cauroselImages.length < 1) {
    const brandFallbackImageArray: any = {};
    brandFallbackImageArray['defaultImageUrl'] = data?.basicInformation?.brand?.photos?.[0]?.content?.[0]?.url;
    brandFallbackImageArray['altText'] = data?.basicInformation?.name ?? '';
    cauroselImages.push(brandFallbackImageArray);
  }
  return cauroselImages;
};
export const generateDetailsPageURL = (
  seoNickname: string,
  brandId: string,
  hotelName: string,
  hotelId: string,
  model: any,
  subdirectory: any
) => {
  const hwsURL = seoNickname ?? getFormattedHotelSlug(hotelId, hotelName);
  const detailsPage = buildPropertyPageURL(brandId, hwsURL, subdirectory, `${SM_VIEW_HOTEL_WEBSITE_LINK}${hwsURL}`);
  const footerlinks = {
    href: detailsPage,
    target: '_blank',
    custom_click_track_value: `${model?.id}|${model?.storefrontPropertyHotelDetails}|${EXTERNAL_CTA_TYPE}`,
    rel: 'noreferrer',
    className: 'm-link-tertiary-button outlet-resevation-link custom_click_track ',
    buttonCopy: `${model?.storefrontPropertyHotelDetails}`,
    isLink: true,
    onClick: () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      (): Window | null => window.open(`${detailsPage}`, '_self');
    },
  };
  return footerlinks;
};
const getRatesNode = (hotel: any) => {
  return hotel?.node?.rates?.[0];
};
const calculateRates = (LARNode: any, isDTT?: any) => {
  const Enable_DTT_queries = isDTT; //TBD
  const rateStatus = LARNode?.status?.code ?? 'unavailable';
  let price = 0;
  if (rateStatus === 'available' || rateStatus === 'AvailableForSale') {
    let cashValue = 0;
    let decimalValue = 0;
    if (!Enable_DTT_queries) {
      cashValue =
        LARNode?.rateAmounts?.[0]?.amountPlusMandatoryFees?.origin?.value ??
        LARNode?.rateAmounts?.[0]?.amount?.origin?.value;

      decimalValue = LARNode?.rateAmounts?.[0]?.amountPlusMandatoryFees?.origin?.value
        ? LARNode?.rateAmounts?.[0]?.amountPlusMandatoryFees?.origin?.valueDecimalPoint
        : LARNode?.rateAmounts?.[0]?.amount?.origin?.value
        ? LARNode?.rateAmounts?.[0]?.amount?.origin?.valueDecimalPoint
        : 0;
    } else {
      cashValue =
        LARNode?.rateModes?.lowestAverageRate?.amountPlusMandatoryFees?.amount ??
        LARNode?.rateModes?.lowestAverageRate?.amount?.amount;

      decimalValue = LARNode?.rateModes?.lowestAverageRate?.amountPlusMandatoryFees?.amount
        ? LARNode?.rateModes?.lowestAverageRate?.amountPlusMandatoryFees?.decimalPoint
        : LARNode?.rateModes?.lowestAverageRate?.amount?.amount
        ? LARNode?.rateModes?.lowestAverageRate?.amount?.decimalPoint
        : 0;
    }
    price = Math.round(cashValue / Math.pow(10, decimalValue as number));
  }
  return {
    price: price,
  };
};
export const getFooterDetails = (
  hotel: any,
  clusterCode: string,
  model: any,
  subdirectory: any,
  showPricesTax?: any,
  isDTT?: any,
  isCategoryRenderer?: boolean,
  enableGroupSearch: boolean = false,
  selectedCurrency?: any
) => {
  const Enable_DTT_queries = isDTT; //TBD
  const localDateFormat = model?.localDateFormat ? model?.localDateFormat : 'DD/MM/YYYY';
  const formatDate = (date: any) => date.toISOString().split('T')[0];

  const propertyId = isCategoryRenderer ? hotel?.node?.id ?? hotel?.id : hotel?.node?.property?.id;

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const startDate = formatDate(today);
  const endDate = formatDate(tomorrow);

  const searchData = {
    startDate: startDate,
    endDate: endDate,
    childAges: [],
    numberInParty: 1,
    quantity: 1,
  };
  let ratePage =
    (subdirectory ? `/${subdirectory}` : '') +
    '/reservation/availabilitySearch.mi?' +
    `&numberOfRooms=${searchData?.quantity}&numberOfAdults=${
      Number(searchData?.numberInParty) - (searchData?.childAges?.length ?? 0)
    }&childrenCount=${searchData?.childAges?.length ?? ''}&childrenAges=${
      searchData?.childAges?.length ? searchData?.childAges.map((i: any) => (Number.isNaN(Number(i)) ? '0' : i)) : ''
    }` +
    `currency=${selectedCurrency?.[0]?.code ?? hotel?.node?.property?.basicInformation?.currency}` +
    '&useRewardsPoints=false&isRateCalendar=true&isSearch=true' +
    '&fromDate=' +
    getDateFormatted(moment(searchData?.startDate), localDateFormat) +
    '&toDate=' +
    getDateFormatted(moment(searchData?.endDate), localDateFormat) +
    `&clusterCode=${clusterCode === '0' ? 'none' : 'corp'}` +
    '&propertyCode=' +
    propertyId;

  // Special logic for category renderer + group search
  if (isCategoryRenderer && enableGroupSearch) {
    // propertyId = hotel?.node?.id ?? hotel?.id;
    ratePage = `/meetings/erfp-schedule-meeting.mi?marshaCode=${propertyId}`;
  }

  const rateObj = getRatesNode(hotel);

  const eeoRateBlock = {};
  const pointsRateBlock = {};
  const isHotelCodeUnavailable = getHotelCodeUnavailable(rateObj, false, pointsRateBlock);
  const { isHotelMaxOccupancyExceeded, isHotelUnavailable } = hotelUnavailableOrMaxOccupied(
    rateObj,
    pointsRateBlock,
    eeoRateBlock,
    false,
    hotel?.node?.property?.basicInformation?.openingDate
  );

  const currentCurrency = selectedCurrency?.[0]?.code ?? hotel?.property?.basicInformation?.currency;

  const ADFRLMratePage = constructAdfOrRlmURL(
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    '',
    '?isRateCalendar=true&propertyCode=',
    false,
    propertyId,
    currentCurrency,
    showPricesTax,
    subdirectory
  );

  let rate;
  // For category renderer, skip price calculation
  if (isCategoryRenderer) {
    rate = null;
  } else if (showPricesTax) {
    const mandatoryFee = getPriceOnToggleSelection(hotel, isDTT);
    const amount = Enable_DTT_queries ? mandatoryFee?.amount : mandatoryFee?.value;
    const decimalPoint = Enable_DTT_queries ? mandatoryFee?.decimalPoint : mandatoryFee?.valueDecimalPoint;
    const totalPrice = mandatoryFee && amount ? Math.round(amount / Math.pow(10, decimalPoint as number)) : 0;
    rate = totalPrice;
  } else {
    const totalrates = calculateRates(rateObj, isDTT);
    rate = totalrates?.price;
  }

  const footerlinks = {
    href: ratePage,
    target: '_self',
    ratesClickTrackValue: `${model?.id}|${propertyId}-${model?.viewRatesLabel}|${EXTERNAL_CTA_TYPE}`,
    rel: 'noreferrer',
    className: 'm-button-s m-button-primary custom_click_track reserve-cta-button',
    text: isCategoryRenderer && enableGroupSearch ? model?.startPlanningLabel : `${model?.viewRatesLabel}`,
    isLink: false,
    // For category renderer + group search, don't show price section
    hasPrice: isCategoryRenderer ? false : rate !== null && rate > 0 ? true : false,
    priceAriaLabel: '',
    priceClickTrackValue: `${model?.id}|${hotel?.node?.property?.id}-${model?.storefrontPropertyPointsPerStay}|${EXTERNAL_CTA_TYPE}`,
    // For category renderer, set priceValue to 1 to pass the truthy check in PropertyCard button condition
    priceValue: isCategoryRenderer ? 1 : rate ?? null,
    priceType: isCategoryRenderer
      ? null
      : rate && `${hotel?.node?.property?.basicInformation?.currency} / ${model?.storefrontPropertyNight}`,
    currency: hotel?.node?.property?.basicInformation?.currency,
    unavailableRateText: model?.rateUnavailable,
    isHotelCodeUnavailable: isHotelCodeUnavailable,
    isHotelMaxOccupancyExceeded: isHotelMaxOccupancyExceeded,
    isHotelUnavailable: isHotelUnavailable,
    clickTrackingLoc: 'Phoenix Search Results',
    ADFRLMratePage: ADFRLMratePage,
  };
  return footerlinks;
};
const getPriceOnToggleSelection = (hotel: any, isDTT?: any) => {
  const Enable_DTT_queries = isDTT; //TBD
  let amount;
  if (Enable_DTT_queries) {
    const lowestAverageRate = hotel?.node?.rates?.[0]?.rateModes?.lowestAverageRate;
    amount = lowestAverageRate?.totalAmount;
  } else {
    amount =
      hotel?.node?.rates?.[0]?.rateAmounts?.[0]?.totalAmount ??
      hotel?.node?.rates?.[0]?.rateAmounts?.[0]?.amount?.origin;
  }

  return amount;
};

const getAmountPlusMandatoryFeeDetails = (
  hotel: any,
  acceptLanguage: any,
  countryCode: any,
  showPricesTax: any,
  isDTT?: any
) => {
  const Enable_DTT_queries = isDTT; //TBD
  const rateObj = getRatesNode(hotel);
  const rateStatus = rateObj?.status?.code ?? 'unavailable';
  let mandatoryFee;

  if (rateStatus === 'available' || rateStatus === 'AvailableForSale') {
    if (showPricesTax) {
      mandatoryFee = getPriceOnToggleSelection(hotel, isDTT);
    } else {
      let feeObj;
      if (acceptLanguage.toLocaleLowerCase() === 'ja-jp' || countryCode === 'JP') {
        feeObj = Enable_DTT_queries
          ? rateObj?.rateModes?.lowestAverageRate?.amount
          : rateObj?.rateAmounts?.[0]?.amount?.origin;
      } else {
        feeObj = Enable_DTT_queries
          ? rateObj?.rateModes?.lowestAverageRate?.amountPlusMandatoryFees ??
            rateObj?.rateModes?.lowestAverageRate?.amount
          : rateObj?.rateAmounts?.[0]?.amountPlusMandatoryFees?.origin ?? rateObj?.rateAmounts?.[0]?.amount?.origin;
      }
      mandatoryFee = feeObj ?? {};
    }
  }
  const amount = Enable_DTT_queries ? mandatoryFee?.amount : mandatoryFee?.value;
  const decimalPoint = Enable_DTT_queries ? mandatoryFee?.decimalPoint : mandatoryFee?.valueDecimalPoint;
  const totalPrice = mandatoryFee && amount ? Math.round(amount / Math.pow(10, decimalPoint as number)) : 0;
  return totalPrice;
};
const getMandatoryFeeDetails = (hotel: any, mandatoryFeesEnabled: boolean, isDTT?: any) => {
  if (!mandatoryFeesEnabled) {
    return;
  }
  const Enable_DTT_queries = isDTT;
  const rateObj = getRatesNode(hotel);
  const mandatoryFee = Enable_DTT_queries
    ? rateObj?.rateModes?.lowestAverageRate?.mandatoryFees
    : rateObj?.rateAmounts?.[0]?.mandatoryFees?.origin;
  const mandatoryFeeDTT = {
    currency: mandatoryFee?.currency,
    value: mandatoryFee?.value ?? mandatoryFee?.amount,
    valueDecimalPoint: mandatoryFee?.decimalPoint ?? mandatoryFee?.valueDecimalPoint,
  };
  return mandatoryFeeDTT;
};
export const getMandatoryFeeDescription = (descriptions: any) => {
  const mandatoryFeeDescObj =
    descriptions && descriptions.length > 0
      ? descriptions.filter((item: { type: { enumCode: string } }) => {
          const code = item?.type?.enumCode ?? '';
          if (code === RESORT_FEE_DESCRIPTION) {
            return true;
          } else if (code === SURCHARGE_ORDINANCE_COST_DESCRIPTION) {
            return true;
          } else if (code === DESTINATION_FEE_DESCRIPTION || code === DESTINATION_FEE_CODE_DESCRIPTION) {
            return true;
          } else if (code === TOURISM_MARKETING_FEE_DESCRIPTION) {
            return true;
          }
          return false;
        })
      : [];
  return mandatoryFeeDescObj;
};
//modify card data helper functions
export const modifyPropertyCardData = (
  resp: any,
  isStateOrCountryEnabled: any,
  model: any,
  normalizedLocale: any,
  currentLocale: any,
  mandatoryFeesEnabled: boolean,
  countryCode?: any,
  showPricesTax?: any,
  isDTT?: any,
  viewPort?: any,
  selectedCurrency?: any
) => {
  const Enable_DTT_queries = isDTT;
  const respObj = Enable_DTT_queries
    ? isStateOrCountryEnabled
      ? resp?.data?.search?.lowestAvailableRates?.searchByLocation ??
        resp?.data?.lowestAvailableRates?.searchByLocation ??
        {}
      : resp?.data?.search?.lowestAvailableRates?.searchByGeolocation ??
        resp?.data?.lowestAvailableRates?.searchByGeolocation ??
        {}
    : isStateOrCountryEnabled
    ? resp?.data?.searchLowestAvailableRatesByLocation ?? {}
    : resp?.data?.searchLowestAvailableRatesByGeolocation ?? {};

  const edgeObj = respObj?.edges ?? [];
  const totalProperties = respObj?.total ?? 0;
  const propList = edgeObj;
  const isGridview = model?.enableGridView;
  const cardsPerPage = Number(model?.propertiesPerPage ?? (isGridview ? 12 : 8));

  //Code to generate JSON schema for SEO at server side
  const jsonLdData = constructJsonSchema(edgeObj, cardsPerPage);

  const warningCode1001 = findWarningIndex(respObj.status?.[0]?.warnings ?? [], WARN_CODE_1001);
  const noRatesAvailableForAnyProperty = warningCode1001 > -1;

  const hotels = propList.length
    ? propList.map((hotel: any) => {
        const {
          fullyRenovatedLabel,
          recentlyRenovatedLabel,
          newTagLabel,
          adultsOnlyLabel,
          renovatedLobbyLabel,
          renovatedRoomsLabel,
        } = model || {};
        const { basicInformation } = hotel?.node?.property || {};
        const isFullyRenovated = basicInformation?.isFullyRenovated;
        const isRecentlyRenovated = basicInformation?.isRecentlyRenovated;
        const hasRenovatedRooms = basicInformation?.hasRenovatedRooms;
        const newProperty = basicInformation?.newProperty;
        const isAdultsOnly = basicInformation?.isAdultsOnly;
        const newLobby = basicInformation?.newLobby;
        const isHotelBookable = basicInformation?.bookable;
        const isAdultOnlyEdgeCaseTriggered = false;
        const isSpecialRateSearch = false;
        const { isResort, isLACountyFee, isMarketingFee, isDestinationFee } =
          identifyTypeOfMandatoryFee(basicInformation);
        const mandatoryFeeTitle = constructMandatoryFeeTitle(
          isResort,
          isLACountyFee,
          isMarketingFee,
          isDestinationFee,
          model
        );
        const mandatoryFeeMessages = {
          title: constructToolTipTitle(isResort, isLACountyFee, isMarketingFee, isDestinationFee, model),
          feeDescription1: model?.includesLabel ?? INCLUDES_LABEL,
          feeDescription2: mandatoryFeeTitle ?? model?.feeDescription2,
          viewRatesLabel: model?.viewRatesLabel ?? VIEW_RATE_LABEL,
        };
        let ribbonDetails = null;
        if (isFullyRenovated) {
          ribbonDetails = {
            ribbonText: fullyRenovatedLabel,
            ribbonClass: 'fully-renovated-ribbon',
          };
        } else if (isRecentlyRenovated) {
          ribbonDetails = {
            ribbonText: recentlyRenovatedLabel,
            ribbonClass: 'recently-renovated-ribbon',
          };
        } else if (newProperty) {
          ribbonDetails = {
            ribbonText: newTagLabel,
            ribbonClass: 'new-hotel-ribbon ribbon',
          };
        } else if (isAdultsOnly) {
          ribbonDetails = {
            ribbonText: adultsOnlyLabel,
            ribbonClass: 'adult-hotel-ribbon',
          };
        } else if (newLobby) {
          ribbonDetails = {
            ribbonText: renovatedLobbyLabel,
            ribbonClass: 'renovated-lobby-ribbon',
          };
        } else if (hasRenovatedRooms) {
          ribbonDetails = {
            ribbonText: renovatedRoomsLabel,
            ribbonClass: 'renovated-rooms-ribbon',
          };
        }
        return {
          id: hotel?.node?.property?.id ?? '',
          sid: hotel?.node?.property?.basicInformation?.marshaBrandCode ?? '',
          brandCode: hotel?.node?.property?.basicInformation?.brand?.id ?? '',
          titleDetails: getTitle(
            hotel?.node?.property,
            hotel?.node?.property?.basicInformation?.brand?.id,
            model,
            normalizedLocale
          ),
          reviewsDetails: getReviewDetails(
            hotel?.node?.property,
            hotel?.node?.property?.basicInformation?.brand?.id,
            hotel?.node?.distance,
            model,
            normalizedLocale,
            currentLocale
          ),
          description: getDescriptionText(hotel?.node?.property?.basicInformation?.descriptions),
          images: getPropertyImages(hotel?.node?.property, viewPort),
          tertiaryLinkDetails: generateDetailsPageURL(
            hotel?.node?.property?.seoNickname ?? '',
            hotel?.node?.property?.basicInformation?.brand?.id,
            hotel?.node?.property?.basicInformation?.name,
            hotel?.node?.property?.id,
            model,
            normalizedLocale
          ),
          footerLinkDetails: getFooterDetails(
            hotel,
            '0',
            model,
            normalizedLocale,
            showPricesTax,
            isDTT,
            false, //isCategoryRenderer
            false, //enableGroupSearch
            selectedCurrency
          ),
          mandatoryFeeObj: {
            amountPlusMandatoryFee: getAmountPlusMandatoryFeeDetails(
              hotel,
              currentLocale,
              countryCode,
              showPricesTax,
              isDTT
            ),
            mandatoryFee: getMandatoryFeeDetails(hotel, mandatoryFeesEnabled, isDTT),
            mandatoryFeeDescription: getMandatoryFeeDescription(hotel?.node?.property?.basicInformation?.descriptions),
            ...mandatoryFeeMessages,
          },
          brandDetails: {
            brandId: hotel?.node?.property?.basicInformation?.brand?.id,
            label: '',
            hasUniquePropertyLogo: hotel?.node?.property?.basicInformation?.hasUniquePropertyLogo,
          },
          ribbonDetails: ribbonDetails,
          isHotelBookable: isHotelBookable,
          isAdultOnlyEdgeCaseTriggered: isAdultOnlyEdgeCaseTriggered,
          isSpecialRateSearch: isSpecialRateSearch,
          noRatesAvailableForAnyProperty: noRatesAvailableForAnyProperty,
        };
      })
    : [];
  return {
    hotels: hotels,
    totalProperties: totalProperties,
    jsonLdData: jsonLdData,
  };
};

export const getPageCardCountInfo = (cardsTotalCount: number, currentPage: any, model: any): string => {
  const cardsPerPage = model?.propertiesPerPage ? Number(model?.propertiesPerPage) : 8;
  const total = Math.ceil(cardsTotalCount / cardsPerPage);
  const start = total === 1 || currentPage === 1 ? 1 : (currentPage - 1) * cardsPerPage + 1;
  const end = total === currentPage || currentPage === cardsTotalCount ? cardsTotalCount : currentPage * cardsPerPage;
  const countText = `${model?.showing} ${start}-${end} ${model?.of} ${cardsTotalCount} ${model?.hotel}`;
  return countText;
};

export const findWarningIndex = (warnings: any, code: string): number => {
  return warnings?.findIndex((warning: any) => warning?.code === code);
};
export const isTooManyProperties = (data: any, isStateOrCountryEnabled: boolean, isDTT: boolean) => {
  const warnings = isDTT
    ? isStateOrCountryEnabled
      ? data?.search?.lowestAvailableRates?.searchByLocation?.status?.[0]?.warnings ?? []
      : data?.search?.lowestAvailableRates?.searchByGeolocation?.status?.[0]?.warnings ?? []
    : isStateOrCountryEnabled
    ? data?.searchLowestAvailableRatesByLocation?.status?.[0]?.warnings ?? []
    : data?.searchLowestAvailableRatesByGeolocation?.status?.[0]?.warnings ?? [];
  const warningCode1009 = findWarningIndex(warnings, WARN_SEARCH_1009);
  return warningCode1009 > -1 ? false : true;
};

export const getProcessedData = (
  propertiesListdata: any,
  isStateOrCountryEnabled: any,
  model: any,
  normalizedLocale: any,
  currentLocale: any,
  currentPage: any,
  mandatoryFeesEnabled: boolean,
  countryCode?: any,
  ariesOffer?: any,
  isDTT?: any,
  viewPort?: any,
  defaultTaxFlag: boolean = false,
  hasdestinationChanged?: boolean,
  defaultCurrency?: any
) => {
  const selectedCurrency = hasdestinationChanged ? defaultCurrency : ariesOffer?.['selectedCurrency'];
  const modifiedPropertyCardData = modifyPropertyCardData(
    propertiesListdata,
    isStateOrCountryEnabled,
    model,
    normalizedLocale,
    currentLocale,
    mandatoryFeesEnabled,
    countryCode,
    defaultTaxFlag,
    isDTT,
    viewPort,
    selectedCurrency
  );
  console.log(`[Destination PropertiesList] modifiedPropertyCardData: ${JSON.stringify(modifiedPropertyCardData)}`);
  const cardCountText = getPageCardCountInfo(modifiedPropertyCardData.totalProperties, currentPage, model);
  const showPricefilter = isTooManyProperties(propertiesListdata?.data, isStateOrCountryEnabled, isDTT);
  const showAvailabilityFilter = isTooManyProperties(propertiesListdata?.data, isStateOrCountryEnabled, isDTT);
  return {
    hotels: modifiedPropertyCardData?.hotels,
    totalProperties: modifiedPropertyCardData.totalProperties,
    jsonLdData: modifiedPropertyCardData.jsonLdData,
    cardCountText: cardCountText,
    showPricesTax: defaultTaxFlag,
    selectedCurrency: selectedCurrency,
    currencyConversionRate: propertiesListdata?.currencyConversionRate,
    showPricefilter: showPricefilter,
    showAvailabilityFilter: showAvailabilityFilter,
  };
};
export const getCommaSeparator = (numb: number, lang: string) => {
  return numb ? Number(Math.floor(numb))?.toLocaleString(lang || '') : null;
};

export const priceDimensionMapping = {
  'Any Price': [],
  '0 - 100 USD': ['1'],
  '100 - 200 USD': ['2'],
  '200+ USD': ['3'],
};

export const formatNumberByLocale = (numb: number | string, lang: string): string => {
  const { log } = logger({})('formatNumberByLocale');
  log.debug(
    `[PerfTesting][utils] Entering the formatNumberByLocale lang:${lang} function at time: ${new Date().getHours()} ${new Date().getMinutes()} ${new Date().getSeconds()} ${new Date().getMilliseconds()}`
  );
  const inputStr = numb ?? '';
  // convert input number to locale string according to the lang
  let str = '';
  switch (lang) {
    case 'ko-KR':
    case 'ja-JP':
    case 'it-IT':
    case 'fr-FR':
    case 'de-DE':
      str = Number(inputStr)?.toLocaleString(lang || '', { maximumFractionDigits: 2 });
      break;
    case 'ru-RU':
      str = Number(inputStr)?.toLocaleString(lang || '', { maximumFractionDigits: 2 });
      break;
    case 'pt-BR':
      str = Number(inputStr)?.toLocaleString(lang || '', { maximumFractionDigits: 3 });
      break;
    case 'es-ES':
      str = Number(inputStr)?.toLocaleString(lang || '', { maximumFractionDigits: 2, useGrouping: true });
      break;
    case '':
    case undefined:
      str = Number(inputStr)?.toLocaleString();
      break;
    default:
      str = Number(inputStr)?.toLocaleString(lang || '');
      break;
  }
  log.debug(
    `[PerfTesting][utils] Exiting the formatNumberByLocale lang:${lang} function at time: ${new Date().getHours()} ${new Date().getMinutes()} ${new Date().getSeconds()} ${new Date().getMilliseconds()}`
  );
  return str;
};

export const getUserAgentType = (userAgent: string): string => {
  const userAgentType = userAgent.toLowerCase();
  console.log(userAgentType, 'userAgentType in common util');
  if (userAgentType.indexOf('android') > -1 || userAgentType.indexOf('iphone') > -1) {
    return 'mobile';
  } else {
    return 'desktop';
  }
};

export const getEligibleBucketCodes = (buckets: any[] | undefined, minPropertyCount: number): Set<string> => {
  const eligible = new Set<string>();
  for (const bucket of buckets ?? []) {
    if (bucket?.code && (bucket?.count ?? 0) >= minPropertyCount) {
      eligible.add(bucket.code);
    }
  }
  return eligible;
};

export const getAccordionData = (model: any, facetsData: any[], minPropertyCount: number) => {
  // If API facets are unavailable, keep only static sections (sections without a filter key).
  if (!facetsData || facetsData.length === 0) {
    return model?.sectionList?.filter((item: any) => !item.filter) ?? [];
  }

  const sectionsWithFilteredLinks = model?.sectionList?.map((sectionItem: { filter: any; linkList: any[] }) => {
    // Static sections are passed through as-is.
    if (!sectionItem.filter) return sectionItem;

    const matchingFacet = facetsData?.find(facet => facet?.type?.code === sectionItem?.filter);

    // If no matching facet exists for this section filter, remove this section.
    if (!matchingFacet) return null;

    const eligibleFilterCodes = getEligibleBucketCodes(matchingFacet?.buckets, minPropertyCount);

    const filteredLinkList = sectionItem?.linkList?.filter((linkItem: { filter: string }) =>
      eligibleFilterCodes?.has(linkItem?.filter)
    );

    return {
      ...sectionItem,
      linkList: filteredLinkList,
    };
  });

  return (
    sectionsWithFilteredLinks?.filter((sectionItem: { filter: any; linkList: string | any[] }) => {
      if (!sectionItem) return false;

      // Keep static sections and remove dynamic sections that have no surviving links.
      if (!sectionItem.filter) return true;

      return sectionItem.linkList?.length > 0;
    }) ?? []
  );
};

export const generatePayloadFacetsVariable = (metadata: any) => {
  const payload: any = {
    search: {
      options: {
        numberInParty: 1,
        numberOfGuestRooms: 1,
      },
      facets: {
        terms: [
          {
            type: 'AMENITIES',
            dimensions: [],
          },
          {
            type: 'ACTIVITIES',
            dimensions: [],
          },
          {
            type: 'PROPERTY_TYPES',
            dimensions: [],
          },
        ],
      },
    },
  };

  // If state is enabled
  if (metadata.isStateEnabled && metadata.stateCode) {
    payload.search.facets.terms.push({
      type: 'STATES',
      dimensions: [metadata.stateCode],
    });
  }

  // If country is enabled
  if (metadata.isCountryEnabled && metadata.countryCode) {
    payload.search.facets.terms.push({
      type: 'COUNTRIES',
      dimensions: [metadata.countryCode],
    });
  }

  // If neither state nor country is enabled, use lat/long
  if (!metadata.isStateEnabled && !metadata.isCountryEnabled && metadata.latitude && metadata.longitude) {
    payload.search.latitude = parseFloat(metadata.latitude);
    payload.search.longitude = parseFloat(metadata.longitude);
  }

  return payload;
};

export const shouldRedirectThemePage = (metadata: any, facetsData: any) => {
  const minPropertyCount = metadata?.minPropertyCount ?? 0;

  // Early return if facetsData is invalid
  if (!facetsData || !Array.isArray(facetsData) || facetsData.length === 0) {
    return true; // Redirect if no facets data
  }

  // Build a set of eligible bucket codes across all facets
  const allBuckets = facetsData.flatMap((facet: any) => (Array.isArray(facet?.buckets) ? facet.buckets : []));
  const eligibleCodes = getEligibleBucketCodes(allBuckets, minPropertyCount);
  console.log('Eligible Bucket Codes:', eligibleCodes);

  if (metadata?.themeType && Array.isArray(metadata.themeType)) {
    for (const theme of metadata.themeType) {
      const items = metadata[theme];
      if (!items || !Array.isArray(items) || items.length === 0) continue;

      for (const item of items) {
        if (!eligibleCodes.has(item)) {
          console.log(`Redirect needed: ${item} not eligible (threshold: ${minPropertyCount})`);
          return true;
        }
      }
    }
  }
  return false;
};

// Add stubs for other helpers to be filled in next
export const getTitleClientSide = (property: any, brandId: string, subdirectory: string, model: any) => {
  const seoNickname = property?.seoNickname ?? '';
  const slug = seoNickname || getFormattedHotelSlug(property?.id, property?.basicInformation?.name);
  const titleLink = buildPropertyPageURL(brandId, slug, subdirectory, `${SM_VIEW_HOTEL_WEBSITE_LINK}${slug}`);
  return {
    title: property?.basicInformation?.name,
    titleAriaLabel: property?.basicInformation?.name,
    isTitleLink: true,
    titleClickCallback: () => {
      setRecentlyViewedProperties(property);
      window.open(titleLink, '_blank');
    },
    titleLink,
    titleClickTrackVal: `${model?.id}|${property?.id}|${EXTERNAL_CTA_TYPE}`,
  };
};

export const getReviewDetailsClientSide = (
  property: any,
  brandId: string,
  distance: any,
  subdirectory: string,
  model: any,
  currentLocaleVal: string,
  getCalculatedDistance: any
) => {
  const seoNickname = property?.seoNickname;
  const rating = property?.reviews?.stars?.count ?? 0;
  const reviewCount = property?.reviews?.numberOfReviews?.count ?? 0;
  const reviewUrl =
    brandId == 'RZ'
      ? `https://www.ritzcarlton.com/en/hotels/${seoNickname}/overview/`
      : (subdirectory ? `/${subdirectory}` : '/en-us') + `/hotels/${seoNickname}/reviews/`;
  const distanceInMiles = getCalculatedDistance(distance, currentLocaleVal !== 'en-US', currentLocaleVal);
  return {
    ...(rating !== 0 && { reviewsAvg: rating }),
    ...(reviewCount !== 0 && { reviewsText: `(${reviewCount} ${model?.storefrontPropertyReviews})` }),
    reviewsLink: reviewUrl,
    reviewsLabel: '',
    reviewsClickTrackValue: `${model?.id}|${property?.id}-${model?.storefrontPropertyReviews}|${EXTERNAL_CTA_TYPE}`,
    milesText:
      distanceInMiles !== null && distanceInMiles !== undefined
        ? `${distanceInMiles} ${model?.unitFromDestinationLabel}`
        : '',
  };
};

export const getLowestRegularRateText = (
  startDate: string,
  endDate: string,
  model: {
    subHeaderText: string;
    dateFormat: string;
  },
  currentLocale: string
) => {
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);
  //To-Do Remove fallback values once AEM changes are completed
  const dateFormat = model?.dateFormat || 'ddd, MMM D';
  const subHeaderText = model?.subHeaderText || 'Lowest Regular Rates for {dates}';
  const formattedStart = getCustomFormattedDate(
    getLocalizedDate(startDateMoment, dateFormat, currentLocale),
    currentLocale
  );
  const formattedEnd = getCustomFormattedDate(
    getLocalizedDate(endDateMoment, dateFormat, currentLocale),
    currentLocale
  );
  const dateRange = `${formattedStart} - ${formattedEnd}`;
  return subHeaderText?.replace('{dates}', dateRange);
};

// Construct URL for Adf or Rlm - View Other Dates CTA
export const constructAdfOrRlmURL = (
  availabilitySearchURL: string,
  navigationADFURL: string,
  navigationRLMURL: string,
  isFlexibleDateSearch: boolean,
  propertyID: string,
  currentCurrency: string,
  includeTaxesAndFees: boolean,
  subDirectoryPrefix: string
) => {
  return isFlexibleDateSearch
    ? addSubDirectoryPrefix(`${availabilitySearchURL}${navigationADFURL}`, subDirectoryPrefix) +
        propertyID +
        '&isSearch=true&currency='
    : addSubDirectoryPrefix(`${availabilitySearchURL}${navigationRLMURL}`, subDirectoryPrefix) +
        propertyID +
        '&isSearch=true&currency=' +
        (currentCurrency === '' ? '' : currentCurrency) +
        '&showFullPrice=' +
        includeTaxesAndFees +
        (currentCurrency === '' ? '' : '&redirectedFromSERP=true');
};

//function to determine if hotel is unavailable or max occupied
export const hotelUnavailableOrMaxOccupied = (
  rateNode: any,
  pointsRateBlock: any,
  eeoRateBlock: any,
  isFlexibleDateSearch: boolean,
  openingDate: any
) => {
  let isHotelMaxOccupancyExceeded = false;
  let isHotelUnavailable = false;

  if (
    !isFlexibleDateSearch &&
    (rateNode?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      pointsRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      eeoRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
  ) {
    isHotelMaxOccupancyExceeded = true;
  } else if (
    !isFlexibleDateSearch &&
    !(rateNode?.rateModes?.lowestAverageRate?.amount || pointsRateBlock?.rateModes?.pointsPerUnit?.points) &&
    openingDate
  ) {
    isHotelUnavailable = true;
  }
  return { isHotelMaxOccupancyExceeded, isHotelUnavailable };
};
